import {forwardRef, useCallback, useEffect, useImperativeHandle, useRef} from 'react';
import {CodePreviewProps, CodeSandboxPreview, CompiledInfo, ICodesandboxRef} from '@baidu/code-live-render';
import {useMemo} from 'react';
import dayjs from 'dayjs';
import cx from 'classnames';
import {LogIdMap} from '../../constant';
import {utils as KuUtils} from '@/kuTools/sdk/utils';
import {flexBuilderInstanceObj} from '@/kuTools/utils';
import {VIEW_BLOCK} from '@/hooks/queryViewController/config';
import {isShowCurrentView} from '@/hooks/queryViewController';
import styles from './index.module.scss';
import sdk from '@/sdk';
import {iframeEnvMap} from '../../configs';
import * as store from '@/store';
import {injectMorphoAPI} from '../MorphoAPIInjector';
import {getEditor} from '../MorphoAPIInjector/utils';
import {MorphoEditor} from '@baidu/realtime-editor';
import NotificationConfig from '../OfficalComponents/NotificationConfig';
import {useNotificationHook} from '../OfficalComponents/NotificationConfig/hooks/useNotificationHook';
import {getNotificationConfig, setNotificationConfig, sendNotification} from '../request';
import {envStorageObj} from '../../utils';

import eventBus, {FLEX_BUILDER_CHAT_INPUT_FOCUS, FLEX_BUILDER_NOTIFICATION} from '@/eventBus';
import {getUrlParams} from '@/utils/urlUtils';
export type {CompiledInfo};

const env = process.env.REACT_APP_ENVIRONMENT;
const IframeUrl = env ? iframeEnvMap[env] : iframeEnvMap.online;

interface IProps extends Pick<CodePreviewProps, 'onWheel' | 'onKeyboard' | 'adaptHeight'> {
    code: string;
    builderId: string;
    blockId: string;
    compiledInfo?: CompiledInfo;
    onCompiled?: (compiledInfo: CompiledInfo | null, options?: any) => void;
    onReady?: () => void;
    onError?: (error: Error) => void;
    mode?: 'preview' | 'edit'; // 知识库文档内组件是 preview 模式，全屏编辑时是 edit 模式
    editorProp?: CodePreviewProps;
    readonly?: boolean;
    isArtifactLoading?: boolean;
    hScreen?: boolean; // 随心搭内强制使用height: 100vh；用于演示模式下随心搭满屏
    onSizeChange?: (params: [width: number, height: number]) => void;
    onRetryBtnClick?: () => void;
    showRetryBtn?: boolean;
    creatorUid?: string;
    isMobile?: boolean;
}

export interface CodePreviewComponentRef{
    cleanRangeTrigger: () => (Promise<any>|undefined);
}

export const CodePreviewComponent = forwardRef<CodePreviewComponentRef, IProps>((props, ref) => {
    const {
        code,
        builderId,
        blockId,
        compiledInfo = {compiledCode: '', dependencies: [], style: ''},
        onCompiled,
        onReady,
        onError,
        mode = 'preview',
        readonly = false,
        isArtifactLoading = false,
        hScreen,
        adaptHeight,
        onSizeChange,
        onRetryBtnClick,
        onWheel,
        onKeyboard,
        showRetryBtn = false,
        creatorUid,
        editorProp,
        isMobile = false,
    } = props;

    const emitterRef = useRef<any>(null);

    const {
        showNotification,
        notificationText,
        onNotificationChange,
        onNotificationDestory,
        notificationCallback,
        setShowNotification,
        notificationValue,
        resetState,
    } = useNotificationHook({getNotificationConfig, setNotificationConfig, builderId});

    // artifact 生成过程中，不更新 code，避免重复 compile
    const cachedCodeRef = useRef(code);
    if (!isArtifactLoading) {
        cachedCodeRef.current = code;
    }

    const handleError = useCallback((error: Error) => {
        onError && onError(error);
    }, [onError]);

    const handleCompiled = (data: CompiledInfo | null, options?: any) => {
        onCompiled && onCompiled(data, options);
        resetState();
    };

    const handleReady = () => {
        onReady && onReady();
    };

    const handleClick = () => {
        const flexBuilderData = flexBuilderInstanceObj.get(builderId);
        KuUtils.kuLogger({
            repId: LogIdMap.InteractComponent,
            params: {
                builderId,
                title: flexBuilderData?.title,
                createTime: dayjs(flexBuilderData?.createTime).format('YYYY-MM-DD HH:mm:ss'),
                creatorUid: creatorUid ?? undefined,
            },
        });
    };

    const handleSizeChange = (params: [width: number, height: number]) => {
        onSizeChange?.(params);
    };

    const renderProps = useMemo(() => {
        const props = {
            isMobile,
            readOnly: readonly,
        };
        return props;
    }, [readonly, isMobile]);

    const previewProps =
        mode === 'edit' || !compiledInfo.compiledCode
            ? {code: cachedCodeRef.current}
            : {compiledComponent: compiledInfo};

    const setup = useCallback((bridge: any) => {

        // 获取ugateToken
        bridge.injectApi('Ku.sdk.getUgateToken', async (targetAppKey: string) => {
            const token = await KuUtils.getToken(targetAppKey);
            return token;
        });

        // 获取用户的token
        bridge.injectApi('Ku.sdk.getUserToken', async () => {
            return sdk.as?.user?.token;
        });

        // 获取当前文档url参数
        bridge.injectApi('Ku.getDocUrlParams', () => {
            return getUrlParams();
        });

        // 获取当前随心搭参数
        bridge.injectApi('Ku.getBuilderParams', (key: string) => {
            const rowData = getUrlParams('builders') || '{}';
            const data = JSON.parse(decodeURIComponent(rowData as string));
            return key ? data[key] : data;
        });

        bridge.injectApi('Ku.sdk.getRteToken', async () => {
            return store.getData(store.KEYS.CUR_DOCUMENT_TOKEN).accessToken;
        });

        // 注册 morpho api，可获取和操作文档数据
        injectMorphoAPI(bridge, builderId);

        // 获取随心搭 builderId
        bridge.injectApi('Ku.getBuilderId', () => {
            return builderId;
        });

        bridge.injectApi('Ku.openLink', (url: string) => {
            if (!url) {
                return;
            }
            const jsonData = {
                'APIName': 'BdHiJs.appnative.webview.loadURL',
                'version': 65,
                'data': {
                    'type': 'system',
                    'url': url,
                },
            };

            const jsonString = JSON.stringify(jsonData);
            const base64String = btoa(jsonString);
            const encodedData = encodeURIComponent(base64String);
            const ruliuUrl = `infoflow://APICenter?data=${encodedData}`;
            window.open(ruliuUrl, '_blank');
        });

        // 控制通知组件渲染
        bridge.injectApi('Ku.requestSubscribeNotification', notificationCallback);

        // 随心搭发送通知消息
        bridge.injectApi('Ku.sendNotification', async (options: {
            // 标题
            title: string;
            // 内容，可选
            content?: string;
            // 发送形式：目前仅支持服务号，后续可扩展。可选
            sender?: '';
            // 接收用户的 email
            receiver: string;
            // 消息对应的跳转 url
            redirectUrl: string;
        }) => {
            // 目前仅支持服务号发送
            const {title, receiver} = options || {};
            const redirectUrl = options?.redirectUrl
                || window.location.protocol + '//' + window.location.host
                + (window.location.port ? (':' + window.location.port) : '')
                + window.location.pathname
                + '?block_id=' + blockId;
            const curDocument = store.getData(store.KEYS.CUR_DOCUMENT) || {};
            const {docGuid, name: docName} = curDocument;
            KuUtils.kuLogger({
                repId: LogIdMap.SendNotification,
                params: {
                    builderId,
                    docGuid,
                },
            });
            return new Promise(async (resolve, reject) => {
                try {
                    const result = await sendNotification(builderId, {
                        receiver,
                        title: title,
                        // 暂时不支持自定义 content，如果需要再放开
                        // content: content,
                        url: redirectUrl,
                        docId: docGuid,
                        docName: docName,
                    });
                    if (result) {
                        resolve({status: 'ok'});
                    }
                } catch (e: any) {
                    const err = new Error();
                    err.message = e.returnMessage;
                    reject(err);
                }
            });
        });

        bridge.injectApi('Ku.env.get', (envKey: string) => {
            const envList = envStorageObj.getByBuilderId(builderId);
            if (Array.isArray(envList)) {
                const env = envList.find((item) => item.key === envKey);
                return env?.value || '';
            }
            return '';
        });

        // 页面导航，目前仅支持带锚点在当前页面跳转，不支持导航当前页面路由
        bridge.injectApi('Ku.navigationTo', async (url?: string) => {
            if (typeof url !== 'string') {
                return;
            }
            const {pathname, hash, origin} = new URL(url);
            // 当前页面导航
            const ANCHOR_ID = '#anchor-';
            if (
                location.origin === origin && location.pathname === pathname
                && hash?.startsWith(ANCHOR_ID)
            ) {
                const editor = getEditor();
                if (!editor || typeof editor.MorphoAPI?.scrollTo !== 'function') {
                    return;
                }
                const anchor = hash.slice(ANCHOR_ID.length);
                // fixme 目前分级标题复制链接，使用的是uuid而非blockId，待后续统一使用blockId
                const useBlockId = anchor.startsWith('docyg');
                if (useBlockId) {
                    editor.MorphoAPI.scrollTo(editor, {
                        blockId: anchor,
                    });
                }
                else {
                    document.querySelector(`[data-uuid="${anchor}"]`)?.scrollIntoView();
                }
            }
        });
    }, [blockId, builderId, notificationCallback]);

    // 组件报错，重新生成
    const handleRetryBtnClick = useCallback(() => {
        onRetryBtnClick?.();

        KuUtils.kuLogger({
            repId: LogIdMap.retryBtnClick,
        });
    }, [onRetryBtnClick]);

    const universalListener = useCallback((editorUUID: string, eventName: string, options: any) => {
        const editor = getEditor();
        if (!editor) {
            return;
        }
        if (editorUUID === editor._uuid) {
            emitterRef.current?.emit('morpho-editor-event', {
                editorId: editorUUID,
                eventName,
                options,
            });
        }
    }, []);

    const passEmitter = useCallback((emitter: any) => {

        emitterRef.current = emitter;

        if (!emitterRef.current) {
            const editor = getEditor();
            editor && (MorphoEditor as any).MorphoEventBus?.offUniversal
                && (MorphoEditor as any).MorphoEventBus.offUniversal(universalListener, editor._uuid);
            return;
        }

        const editor = getEditor();
        editor && (MorphoEditor as any).MorphoEventBus?.onUniversal
            && (MorphoEditor as any).MorphoEventBus.onUniversal(universalListener, editor._uuid);

    }, [universalListener]);

    useEffect(() => {
        return () => {
            const editor = getEditor();
            editor && (MorphoEditor as any).MorphoEventBus?.offUniversal
                && (MorphoEditor as any).MorphoEventBus.offUniversal(universalListener, editor._uuid);
            emitterRef.current = null;
        };
    }, [universalListener]);
    // 监听随心搭点击通知设置
    useEffect(() => {
        const listener = (remoteBuilderId: string) => {
            if (builderId !== remoteBuilderId) {
                return null;
            }
            resetState();
            setShowNotification(!showNotification);
        };
        eventBus.on(FLEX_BUILDER_NOTIFICATION, listener);
        return () => {
            eventBus.off(FLEX_BUILDER_NOTIFICATION, listener);
        };
    }, [builderId, resetState, setShowNotification, showNotification]);
    const sandboxPreviewRef = useRef<ICodesandboxRef>(null);

    useEffect(() => {
        const cb = () => {
            sandboxPreviewRef.current?.cleanRangeTrigger?.();
        };
        eventBus.on(FLEX_BUILDER_CHAT_INPUT_FOCUS, cb);
        return () => {
            eventBus.off(FLEX_BUILDER_CHAT_INPUT_FOCUS, cb);
        };
    }, []);

    useImperativeHandle(ref, () => {
        return {
            cleanRangeTrigger: () => {
                return sandboxPreviewRef.current?.cleanRangeTrigger?.();
            },
        };
    }, []);

    const showFlexBuilderNormalMode = isShowCurrentView(VIEW_BLOCK.flexBuilderShowNormalMode);

    return (
        <>
            {showNotification && <NotificationConfig
                mode={mode}
                text={notificationText}
                onDestory={onNotificationDestory}
                onChange={onNotificationChange}
                value={notificationValue}
            />}
            <CodeSandboxPreview
                ref={sandboxPreviewRef}
                id={builderId}
                className={cx(styles.container, !showFlexBuilderNormalMode && styles.onlyContent)}
                iframeUrl={IframeUrl}
                onError={handleError}
                onCompiled={handleCompiled}
                onReady={handleReady}
                {...previewProps}
                data-zoom-disable="true"
                hScreen={hScreen}
                adaptHeight={adaptHeight}
                renderProps={renderProps}
                onClick={handleClick}
                onSizeChange={handleSizeChange}
                setup={setup}
                onErrorRetry={handleRetryBtnClick}
                showErrorRetry={showRetryBtn}
                passEmitter={passEmitter}
                editor={editorProp}
                onKeyboard={onKeyboard}
                onWheel={onWheel}
                allow="camera https://morpho.bj.bcebos.com;
                       microphone https://morpho.bj.bcebos.com;
                       fullscreen https://morpho.bj.bcebos.com;
                       display-capture https://morpho.bj.bcebos.com;
                       geolocation;
                       web-share https://morpho.bj.bcebos.com;"
            />
        </>
    );
});
